<template>
  <div class="editor-page">
    <div class="editor-header">
      <Button
        type="text"
        icon="md-arrow-back"
        class="back-button"
        title="返回"
        @click="handleBack"
      />
      <span class="editor-title">{{ pageTitle }}</span>
    </div>

    <Form ref="formRef" :model="form" :label-width="82" class="base-card">
      <div class="form-line">
        <FormItem
          label="选择模板"
          prop="templateId"
        >
          <Select
            v-model="form.templateId"
            class="template-select"
            placeholder="— 选择策略模板 —"
            filterable
            remote
            clearable
            transfer
            :remote-method="searchTemplateOptions"
            :loading="templateSearchLoading || templateDetailLoading"
            :disabled="readonly"
            @on-change="handleTemplateChange"
          >
            <Option
              v-for="item in templateOptions"
              :key="item.value"
              :value="item.value"
              :label="item.label"
            >
              {{ item.label }}
            </Option>
          </Select>
        </FormItem>
        <span class="form-tip">选择后自动填充下方配置</span>
      </div>

      <div class="form-line">
        <FormItem
          label="策略名称"
          prop="strategyName"
          :rules="[{ required: true, message: '请输入策略名称' }]"
        >
          <Input
            v-model="form.strategyName"
            class="strategy-input"
            placeholder="请输入策略名称"
            :maxlength="50"
            :disabled="readonly"
          />
        </FormItem>
        <FormItem label="人群包">
          <RadioGroup
            v-model="form.audienceType"
            type="button"
            button-style="solid"
            :disabled="readonly"
            @on-change="handleAudienceTypeChange"
          >
            <Radio label="不限" :disabled="readonly">不限</Radio>
            <Radio label="选择人群" :disabled="readonly">选择人群包</Radio>
          </RadioGroup>
        </FormItem>
        <FormItem
          v-if="form.audienceType === '选择人群'"
          prop="crowdIds"
          :rules="[
            {
              message: '请选择人群包',
              trigger: 'change',
              validator(rule, value, callback) {
                if (!form.crowdIds || !form.crowdIds.length) {
                  callback('请选择人群包')
                } else {
                  callback()
                }
              }
            }
          ]"
        >
          <Select
            v-model="form.crowdIds"
            class="crowd-select"
            placeholder="请输入人群包名称搜索"
            filterable
            multiple
            clearable
            transfer
            :disabled="readonly"
          >
            <Option
              v-for="item in crowdOptions"
              :key="item.id"
              :value="item.id"
              :label="item.name"
            >
              <Poptip
                trigger="hover"
                placement="left"
                transfer
                word-wrap
                width="350"
              >
                <span>{{ item.name }}</span>
                <template #content>
                  <div class="crowd-preview-card">
                    <div class="crowd-preview-title">{{ item.name }}</div>
                    <div class="crowd-preview-body">
                      <div
                        v-if="item.registerTimeDisplay"
                        class="crowd-preview-row"
                      >
                        <span class="crowd-preview-label">注册时间</span>
                        <span class="crowd-preview-value">
                          {{ item.registerTimeDisplay }}
                        </span>
                      </div>
                      <div v-if="item.payTypeName" class="crowd-preview-row">
                        <span class="crowd-preview-label">付费类型</span>
                        <span class="crowd-preview-value">
                          {{ item.payTypeName }}
                        </span>
                      </div>
                      <div
                        v-if="
                          item.todayLogin !== null &&
                          item.todayLogin !== undefined
                        "
                        class="crowd-preview-row"
                      >
                        <span class="crowd-preview-label">当日登录</span>
                        <span class="crowd-preview-value">
                          {{ item.todayLogin ? '是' : '否' }}
                        </span>
                      </div>
                      <div v-if="item.deviceSysName" class="crowd-preview-row">
                        <span class="crowd-preview-label">操作设备</span>
                        <span class="crowd-preview-value">
                          {{ item.deviceSysName }}
                        </span>
                      </div>
                      <div v-if="item.channelNames" class="crowd-preview-row">
                        <span class="crowd-preview-label">渠道</span>
                        <span class="crowd-preview-value">
                          {{ item.channelNames }}
                        </span>
                      </div>
                      <div v-if="item.langNames" class="crowd-preview-row">
                        <span class="crowd-preview-label">语言</span>
                        <span class="crowd-preview-value">
                          {{ item.langNames }}
                        </span>
                      </div>
                      <div v-if="item.countryNames" class="crowd-preview-row">
                        <span class="crowd-preview-label">国家</span>
                        <div class="crowd-country-text-wrapper">
                          <Tooltip
                            style="width: 100%; display: block"
                            :content="item.countryNames"
                            :max-width="300"
                            placement="top"
                            transfer
                          >
                            <span class="crowd-preview-value ellipsis-text">
                              {{ item.countryNames }}
                            </span>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </Poptip>
            </Option>
          </Select>
        </FormItem>
      </div>
    </Form>

    <div class="config-card">
      <Tabs v-model="activePlatform" :animated="false">
        <TabPane label="IOS 配置" name="ios" />
        <TabPane label="Android 配置" name="android" />
      </Tabs>

      <div class="section-title">充值面板配置</div>
      <div class="payment-grid">
        <div
          v-for="panel in currentConfig.panels"
          :key="activePlatform + '-' + panel.key"
          class="payment-card"
        >
          <div class="payment-title">{{ panel.title }}</div>
          <div class="switch-box">
            <i-switch v-model="panel.thirdPay" :disabled="readonly" />
            <span>三方支付</span>
          </div>

          <div v-show="panel.thirdPay">
            <RadioGroup v-model="panel.payLinkType" class="radio-row">
              <Radio :label="0" :disabled="readonly">聚合</Radio>
              <Radio :label="1" :disabled="readonly">分步</Radio>
            </RadioGroup>

            <div class="table-title">
              <span>支付方式</span>
              <Button
                size="small"
                :disabled="readonly"
                @click="addPayMethod(panel)"
              >
                + 新增
              </Button>
            </div>
            <div class="method-table">
              <div class="method-head">
                <span>序</span>
                <span>支付方式</span>
                <span>渠道</span>
                <span>默认</span>
                <span></span>
              </div>
              <draggable
                v-model="panel.payMethods"
                handle=".drag-handle"
                :disabled="readonly"
                :animation="150"
              >
                <div
                  v-for="(row, index) in panel.payMethods"
                  :key="row.id"
                  class="method-row"
                >
                  <span class="drag-handle">≡</span>
                  <Select
                    v-model="row.payMethod"
                    size="small"
                    transfer
                    :disabled="readonly || row.payMethod === 0"
                    @on-change="onMethodChange(panel, index, $event)"
                  >
                    <Option
                      v-for="item in getMethodOptions(activePlatform)"
                      :key="activePlatform + '-method-' + item.dictValue"
                      :value="Number(item.dictValue)"
                      :disabled="
                        isMethodUsed(panel, Number(item.dictValue), index)
                      "
                    >
                      {{ item.dictName }}
                    </Option>
                  </Select>
                  <Select
                    v-model="row.provider"
                    size="small"
                    transfer
                    :disabled="readonly || row.payMethod === 0"
                  >
                    <Option
                      v-for="item in getFilteredProviders(panel, index)"
                      :key="activePlatform + '-provider-' + item.dictValue"
                      :value="Number(item.dictValue)"
                      :disabled="
                        (isWalletSelected(panel, index) &&
                          item.dictName !== 'PayPal') ||
                        (isAppleOrGooglePaySelected(panel, index) &&
                          item.dictName === 'PayPal')
                      "
                    >
                      {{ item.dictName }}
                    </Option>
                  </Select>
                  <Radio
                    :value="row.isDefault"
                    :disabled="readonly"
                    @on-change="setDefaultPayMethod(panel, index)"
                  />
                  <a
                    v-if="
                      panel.payMethods.length > 1 &&
                      row.payMethod !== 0 &&
                      !readonly
                    "
                    class="row-delete"
                    @click="removePayMethod(panel, index)"
                  >
                    ×
                  </a>
                </div>
              </draggable>
            </div>
          </div>

          <div v-if="panel.supportsGoods" class="goods-config-table">
            <draggable
              v-model="panel.goodsSections"
              handle=".group-handle"
              :disabled="readonly || !panel.supportsGold"
              :animation="150"
            >
              <div
                v-for="section in getPanelGoodsSections(panel)"
                :key="section.key"
                class="goods-section"
              >
                <template v-if="section.key === 'subscription'">
            <div class="goods-group-title">
              <span class="drag-handle group-handle">≡</span>
              订阅付费项
            </div>
            <draggable
              v-model="panel.subscriptionGoods"
              handle=".row-handle"
              :disabled="readonly"
              :animation="150"
            >
              <div
                v-for="(row, index) in panel.subscriptionGoods"
                :key="'subscription-' + row.id"
                class="subscription-item"
              >
                <div
                  class="subscription-main"
                  :class="{ 'without-third': !panel.thirdPay }"
                >
                  <span class="drag-handle row-handle">≡</span>
                  <Poptip
                    v-model="row.visible"
                    class="goods-picker-poptip"
                    trigger="click"
                    transfer
                    placement="bottom-start"
                    popper-class="recharge-subscription-popper"
                    width="560"
                    padding="0"
                    :disabled="readonly"
                  >
                    <div class="goods-picker-trigger-wrap">
                      <div
                        class="goods-picker-trigger"
                        :class="{ disabled: readonly }"
                      >
                        <Tag
                          v-if="row.goodsId"
                          :color="row.goodsType === 1 ? 'blue' : 'orange'"
                        >
                          {{ row.goodsType === 1 ? 'VIP' : '通行证' }}
                        </Tag>
                        <span class="trigger-text">
                          {{ getGoodsTriggerText(row, activePlatform) }}
                        </span>
                      </div>
                      <div
                        v-if="getSubscriptionTipGoods(row, activePlatform)"
                        class="goods-mini-preview"
                      >
                        <div class="goods-mini-title">
                          {{ getSubscriptionTipGoods(row, activePlatform).goodsName }}
                        </div>
                        <div
                          v-for="item in getSubscriptionTipRows(
                            getSubscriptionTipGoods(row, activePlatform),
                            activePlatform
                          )"
                          :key="item.key"
                          class="goods-mini-line"
                        >
                          <span>{{ item.label }}</span>
                          <strong :class="item.className">
                            {{ item.value }}
                          </strong>
                        </div>
                      </div>
                    </div>
                    <div slot="content" class="subscription-picker">
                      <div class="picker-tabs">
                        <span
                          :class="{ active: row.goodsType === 1 }"
                          @click.stop="setSubscriptionType(row, 1)"
                        >
                          VIP
                        </span>
                        <span
                          :class="{ active: row.goodsType === 2 }"
                          @click.stop="setSubscriptionType(row, 2)"
                        >
                          通行证
                        </span>
                      </div>
                      <div class="picker-content">
                        <div class="picker-list">
                          <div
                            v-for="item in getSubscriptionOptions(
                              activePlatform,
                              row
                            )"
                            :key="'subscription-option-' + item.id"
                            class="picker-item"
                            :class="{
                              selected: isSameGoodsId(row.goodsId, item.id),
                              disabled: isSubscriptionGoodsUsed(
                                panel.subscriptionGoods,
                                row.goodsType,
                                item.id,
                                index
                              )
                            }"
                            @mouseenter="setPreviewGoods(row, item.id)"
                            @click.stop="
                              !isSubscriptionGoodsUsed(
                                panel.subscriptionGoods,
                                row.goodsType,
                                item.id,
                                index
                              ) && selectSubscriptionGoods(row, item)
                            "
                          >
                            <Tag :color="row.goodsType === 1 ? 'blue' : 'orange'">
                              {{ row.goodsType === 1 ? 'VIP' : '通行证' }}
                            </Tag>
                            <span class="picker-name">{{ item.goodsName }}</span>
                            <span class="picker-price">
                              {{ formatGoodsPrice(item) }}
                            </span>
                          </div>
                          <div
                            v-if="
                              !getSubscriptionOptions(activePlatform, row).length
                            "
                            class="empty-text"
                          >
                            暂无付费项
                          </div>
                        </div>
                        <div class="picker-preview">
                          <template v-if="getPreviewGoods(row, activePlatform)">
                            <div class="preview-title">
                              {{ getPreviewGoods(row, activePlatform).goodsName }}
                            </div>
                            <div
                              v-for="item in getSubscriptionTipRows(
                                getPreviewGoods(row, activePlatform),
                                activePlatform
                              )"
                              :key="item.key"
                              class="preview-line"
                            >
                              <span>{{ item.label }}</span>
                              <strong :class="item.className">
                                {{ item.value }}
                              </strong>
                            </div>
                          </template>
                          <div v-else class="empty-text">请选择付费项</div>
                        </div>
                      </div>
                    </div>
                  </Poptip>
                  <Checkbox
                    v-if="panel.thirdPay"
                    v-model="row.onlyThird"
                    :disabled="readonly"
                  >
                    仅三方
                  </Checkbox>
                  <a
                    v-if="panel.subscriptionGoods.length > 1 && !readonly"
                    class="row-delete"
                    @click="removeGoodsRow(panel.subscriptionGoods, index)"
                  >
                    ×
                  </a>
                </div>
                <div
                  v-if="
                    panel.thirdPay &&
                    row.onlyThird
                  "
                  class="subscription-default"
                >
                  <div v-if="isStepThirdPay(panel)" class="step-pay-tip">
                    分步支付模式下，无需选择默认支付方式。
                  </div>
                  <div v-else class="default-line">
                    <label>默认支付方式：</label>
                    <Select
                      v-model="row.defaultPayMethod"
                      size="small"
                      transfer
                      :disabled="readonly"
                      placeholder="支付方式"
                      @on-change="onDefaultPayMethodChange(row, $event)"
                    >
                      <Option
                        v-for="item in getThirdMethodOptions(activePlatform)"
                        :key="'sub-method-' + item.dictValue"
                        :value="Number(item.dictValue)"
                      >
                        {{ item.dictName }}
                      </Option>
                    </Select>
                    <label class="inline-label">默认渠道：</label>
                    <Select
                      v-model="row.defaultProvider"
                      size="small"
                      transfer
                      :disabled="readonly"
                      placeholder="渠道"
                    >
                      <Option
                        v-for="item in getDefaultProviderOptions(row.defaultPayMethod)"
                        :key="'sub-provider-' + item.dictValue"
                        :value="Number(item.dictValue)"
                      >
                        {{ item.dictName }}
                      </Option>
                    </Select>
                  </div>
                </div>
              </div>
            </draggable>
            <div
              v-if="!readonly"
              class="add-row"
              @click="addSubscriptionGoods(panel)"
            >
              + 新增订阅项
            </div>

                </template>

                <template v-else-if="section.key === 'gold' && panel.supportsGold">
              <div class="goods-group-title">
                <span class="drag-handle group-handle">≡</span>
                金币付费项
              </div>
              <draggable
                v-model="panel.goldGoods"
                handle=".row-handle"
                :disabled="readonly"
                :animation="150"
                class="gold-grid"
              >
                <div
                  v-for="(row, index) in panel.goldGoods"
                  :key="'gold-' + row.id"
                  class="gold-item"
                >
                  <Poptip
                    v-model="row.visible"
                    class="goods-picker-poptip"
                    trigger="click"
                    transfer
                    placement="bottom-start"
                    popper-class="recharge-gold-picker-popper"
                    width="560"
                    padding="0"
                    :disabled="readonly"
                  >
                    <div class="goods-picker-trigger-wrap">
                      <div
                        class="goods-picker-trigger"
                        :class="{ disabled: readonly }"
                      >
                        <Tag v-if="row.goodsId" color="orange">金币</Tag>
                        <span class="trigger-text">
                          {{ getGoldTriggerText(row, activePlatform) }}
                        </span>
                      </div>
                      <div
                        v-if="getGoldPreview(row, activePlatform)"
                        class="goods-mini-preview"
                      >
                        <div class="goods-mini-title">
                          {{ getGoldPreview(row, activePlatform).goodsName }}
                        </div>
                        <div
                          v-for="item in getGoldTipRows(
                            getGoldPreview(row, activePlatform)
                          )"
                          :key="item.key"
                          class="goods-mini-line"
                        >
                          <span>{{ item.label }}</span>
                          <strong :class="item.className">
                            {{ item.value }}
                          </strong>
                        </div>
                      </div>
                    </div>
                    <div slot="content" class="subscription-picker gold-picker">
                      <div class="picker-content">
                        <div class="picker-list">
                          <div
                            v-for="item in getGoodsOptions(activePlatform, 'gold')"
                            :key="'gold-option-' + item.id"
                            class="picker-item"
                            :class="{
                              selected: isSameGoodsId(row.goodsId, item.id),
                              disabled: isGoodsUsed(
                                panel.goldGoods,
                                item.id,
                                index
                              )
                            }"
                            @mouseenter="setGoldPreviewGoods(row, item.id)"
                            @click.stop="
                              !isGoodsUsed(panel.goldGoods, item.id, index) &&
                                selectGoldGoods(row, item)
                            "
                          >
                            <Tag color="orange">金币</Tag>
                            <span class="picker-name">{{ item.goodsName }}</span>
                            <span class="picker-price">
                              {{ formatGoodsPrice(item) }}
                            </span>
                          </div>
                          <div
                            v-if="!getGoodsOptions(activePlatform, 'gold').length"
                            class="empty-text"
                          >
                            暂无付费项
                          </div>
                        </div>
                        <div class="picker-preview">
                          <template v-if="getGoldPickerPreview(row, activePlatform)">
                            <div class="preview-title">
                              {{ getGoldPickerPreview(row, activePlatform).goodsName }}
                            </div>
                            <div
                              v-for="item in getGoldTipRows(
                                getGoldPickerPreview(row, activePlatform)
                              )"
                              :key="item.key"
                              class="preview-line"
                            >
                              <span>{{ item.label }}</span>
                              <strong :class="item.className">
                                {{ item.value }}
                              </strong>
                            </div>
                          </template>
                          <div v-else class="empty-text">请选择付费项</div>
                        </div>
                      </div>
                    </div>
                  </Poptip>
                  <a
                    v-if="panel.goldGoods.length > 1 && !readonly"
                    class="row-delete"
                    @click="removeGoldGoodsRow(panel, index)"
                  >
                    ×
                  </a>
                </div>
              </draggable>
              <div
                v-if="!readonly"
                class="add-row"
                @click="addGoldGoods(panel)"
              >
                + 新增金币项
              </div>
                </template>
              </div>
            </draggable>
          </div>

          <div v-if="panel.key === 'paywall'" class="ad-tag-box">
            <div class="ad-tag-title">
              广告解锁层
              <span class="inner-purchase-tag">仅内购</span>
            </div>
            <div class="ad-tag-picker-row">
              <Poptip
                v-model="panel.adTagVisible"
                class="goods-picker-poptip"
                trigger="click"
                transfer
                placement="bottom-start"
                popper-class="recharge-subscription-popper"
                width="560"
                padding="0"
                :disabled="readonly"
              >
                <div class="goods-picker-trigger-wrap">
                  <div
                    class="goods-picker-trigger"
                    :class="{ disabled: readonly }"
                  >
                    <Tag v-if="panel.adTagGoodsId" color="blue">VIP</Tag>
                    <span class="trigger-text">
                      {{ getAdTagTriggerText(panel, activePlatform) }}
                    </span>
                  </div>
                  <div
                    v-if="getAdTagSelectedGoods(panel, activePlatform)"
                    class="goods-mini-preview"
                  >
                    <div class="goods-mini-title">
                      {{ getAdTagSelectedGoods(panel, activePlatform).goodsName }}
                    </div>
                    <div
                      v-for="item in getSubscriptionTipRows(
                        getAdTagSelectedGoods(panel, activePlatform),
                        activePlatform
                      )"
                      :key="'ad-tag-mini-' + item.key"
                      class="goods-mini-line"
                    >
                      <span>{{ item.label }}</span>
                      <strong :class="item.className">
                        {{ item.value }}
                      </strong>
                    </div>
                  </div>
                </div>
                <div slot="content" class="subscription-picker">
                  <div class="picker-content">
                    <div class="picker-list">
                      <div
                        v-for="item in getAdTagGoodsOptions(activePlatform)"
                        :key="'ad-tag-option-' + item.id"
                        class="picker-item"
                        :class="{
                          selected: isSameGoodsId(panel.adTagGoodsId, item.id)
                        }"
                        @mouseenter="setAdTagPreviewGoods(panel, item.id)"
                        @click.stop="selectAdTagGoods(panel, item)"
                      >
                        <Tag color="blue">VIP</Tag>
                        <span class="picker-name">{{ item.goodsName }}</span>
                        <span class="picker-price">
                          {{ formatGoodsPrice(item) }}
                        </span>
                      </div>
                      <div
                        v-if="!getAdTagGoodsOptions(activePlatform).length"
                        class="empty-text"
                      >
                        暂无付费项
                      </div>
                    </div>
                    <div class="picker-preview">
                      <template v-if="getAdTagPickerPreview(panel, activePlatform)">
                        <div class="preview-title">
                          {{ getAdTagPickerPreview(panel, activePlatform).goodsName }}
                        </div>
                        <div
                          v-for="item in getSubscriptionTipRows(
                            getAdTagPickerPreview(panel, activePlatform),
                            activePlatform
                          )"
                          :key="'ad-tag-preview-' + item.key"
                          class="preview-line"
                        >
                          <span>{{ item.label }}</span>
                          <strong :class="item.className">
                            {{ item.value }}
                          </strong>
                        </div>
                      </template>
                      <div v-else class="empty-text">请选择付费项</div>
                    </div>
                  </div>
                </div>
              </Poptip>
              <a
                v-if="panel.adTagGoodsId && !readonly"
                class="row-delete"
                @click="clearAdTagGoods(panel)"
              >
                ×
              </a>
            </div>
          </div>
        </div>
      </div>

      <div class="wide-section">
        <div class="section-title">活动三方配置</div>
        <div class="wide-card">
          <div class="switch-box">
            <i-switch v-model="currentConfig.activity.thirdPay" :disabled="readonly" />
            <span>三方支付</span>
          </div>
          <div v-show="currentConfig.activity.thirdPay" class="activity-content">
            <div class="activity-row">
              <span>活动H5</span>
              <Select
                v-model="currentConfig.activity.pushRuleIds"
                multiple
                transfer
                :disabled="readonly"
              >
                <Option
                  v-for="item in activityOptions"
                  :key="'activity-push-rule-' + item.id"
                  :value="item.id"
                  :label="item.label"
                >
                  {{ item.label }}
                </Option>
              </Select>
            </div>
            <div class="table-title">
              <span>支付方式</span>
              <Button
                size="small"
                :disabled="readonly"
                @click="addPayMethod(currentConfig.activity)"
              >
                + 新增
              </Button>
            </div>
            <div class="method-table simple-table">
              <div class="method-head">
                <span>序</span>
                <span>支付方式</span>
                <span>渠道</span>
                <span>默认</span>
                <span></span>
              </div>
              <draggable
                v-model="currentConfig.activity.payMethods"
                handle=".drag-handle"
                :disabled="readonly"
                :animation="150"
              >
                <div
                  v-for="(row, index) in currentConfig.activity.payMethods"
                  :key="row.id"
                  class="method-row"
                >
                  <span class="drag-handle">≡</span>
                  <Select
                    v-model="row.payMethod"
                    size="small"
                    transfer
                    :disabled="readonly || row.payMethod === 0"
                    @on-change="onMethodChange(currentConfig.activity, index, $event)"
                  >
                    <Option
                      v-for="item in getMethodOptions(activePlatform)"
                      :key="activePlatform + '-act-method-' + item.dictValue"
                      :value="Number(item.dictValue)"
                      :disabled="
                        isMethodUsed(currentConfig.activity, Number(item.dictValue), index)
                      "
                    >
                      {{ item.dictName }}
                    </Option>
                  </Select>
                  <Select
                    v-model="row.provider"
                    size="small"
                    transfer
                    :disabled="readonly || row.payMethod === 0"
                  >
                    <Option
                      v-for="item in getFilteredProviders(currentConfig.activity, index)"
                      :key="activePlatform + '-act-provider-' + item.dictValue"
                      :value="Number(item.dictValue)"
                    >
                      {{ item.dictName }}
                    </Option>
                  </Select>
                  <Radio
                    :value="row.isDefault"
                    :disabled="readonly"
                    @on-change="setDefaultPayMethod(currentConfig.activity, index)"
                  />
                  <a
                    v-if="
                      currentConfig.activity.payMethods.length > 1 &&
                      row.payMethod !== 0 &&
                      !readonly
                    "
                    class="row-delete"
                    @click="removePayMethod(currentConfig.activity, index)"
                  >
                    ×
                  </a>
                </div>
              </draggable>
            </div>
          </div>
        </div>
      </div>

      <div class="wide-section">
        <div class="section-title">折扣弹窗</div>
        <div class="wide-card">
          <div class="switch-box">
            <i-switch v-model="currentConfig.discount.thirdPay" :disabled="readonly" />
            <span>三方支付</span>
          </div>
          <div v-show="currentConfig.discount.thirdPay" class="activity-content">
            <div class="table-title">
              <span>支付方式</span>
              <Button
                size="small"
                :disabled="readonly"
                @click="addPayMethod(currentConfig.discount)"
              >
                + 新增
              </Button>
            </div>
            <div class="method-table simple-table">
              <div class="method-head">
                <span>序</span>
                <span>支付方式</span>
                <span>渠道</span>
                <span>默认</span>
                <span></span>
              </div>
              <draggable
                v-model="currentConfig.discount.payMethods"
                handle=".drag-handle"
                :disabled="readonly"
                :animation="150"
              >
                <div
                  v-for="(row, index) in currentConfig.discount.payMethods"
                  :key="row.id"
                  class="method-row"
                >
                  <span class="drag-handle">≡</span>
                  <Select
                    v-model="row.payMethod"
                    size="small"
                    transfer
                    :disabled="readonly || row.payMethod === 0"
                    @on-change="onMethodChange(currentConfig.discount, index, $event)"
                  >
                    <Option
                      v-for="item in getMethodOptions(activePlatform)"
                      :key="activePlatform + '-dis-method-' + item.dictValue"
                      :value="Number(item.dictValue)"
                      :disabled="
                        isMethodUsed(currentConfig.discount, Number(item.dictValue), index)
                      "
                    >
                      {{ item.dictName }}
                    </Option>
                  </Select>
                  <Select
                    v-model="row.provider"
                    size="small"
                    transfer
                    :disabled="readonly || row.payMethod === 0"
                  >
                    <Option
                      v-for="item in getFilteredProviders(currentConfig.discount, index)"
                      :key="activePlatform + '-dis-provider-' + item.dictValue"
                      :value="Number(item.dictValue)"
                    >
                      {{ item.dictName }}
                    </Option>
                  </Select>
                  <Radio
                    :value="row.isDefault"
                    :disabled="readonly"
                    @on-change="setDefaultPayMethod(currentConfig.discount, index)"
                  />
                  <a
                    v-if="
                      currentConfig.discount.payMethods.length > 1 &&
                      row.payMethod !== 0 &&
                      !readonly
                    "
                    class="row-delete"
                    @click="removePayMethod(currentConfig.discount, index)"
                  >
                    ×
                  </a>
                </div>
              </draggable>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="footer-bar">
      <Button class="footer-button" @click="handleBack">取消</Button>
      <Button
        v-if="!readonly"
        type="primary"
        class="footer-button"
        :loading="saving"
        @click="handleSave"
      >
        保存
      </Button>
    </div>
  </div>
</template>

<script>
import { getCrowdList } from '@/api/crowd'
import { getGoodsClassification } from '@/api/goods'
import { getActivityPushRuleList } from '@/api/activityPush'
import {
  getRechargeTemplateConfigInfo,
  getRechargeTemplateConfigPage
} from '@/api/fee'
import { debounce } from 'lodash'
import draggable from 'vuedraggable'

const PLATFORM_CHANNEL_MAP = {
  ios: 0,
  android: 1
}

const PANEL_ENTRY_MAP = {
  paywall: 0,
  wallet: 1,
  vip: 2
}

const DISCOUNT_ENTRY_ID = 3
const ACTIVITY_ENTRY_ID = 4

const VIP_DURATION_MAP = [
  '一周',
  '四周',
  '一个月',
  '两个月',
  '三个月',
  '四个月',
  '六个月',
  '八个月',
  '一年'
]

const COMMON_DURATION_MAP = {
  7: '一周',
  30: '一个月',
  365: '一年'
}

let payRowId = 0
let goodsRowId = 0

function createDefaultPayMethod() {
  return {
    id: ++payRowId,
    payMethod: 0,
    provider: '',
    isDefault: true
  }
}

function createExtraPayMethod() {
  return {
    id: ++payRowId,
    payMethod: '',
    provider: '',
    isDefault: false
  }
}

function createSubscriptionGoods() {
  return {
    id: ++goodsRowId,
    goodsType: 1,
    goodsId: null,
    onlyThird: false,
    defaultPayMethod: '',
    defaultProvider: '',
    visible: false,
    previewGoodsId: null
  }
}

function createGoldGoods() {
  return {
    id: ++goodsRowId,
    isLocal: true,
    goodsType: 0,
    goodsId: null,
    visible: false,
    previewGoodsId: null
  }
}

function createGoldGoodsRow() {
  return [createGoldGoods(), createGoldGoods()]
}

function createGoodsSections(supportsGold = true) {
  return supportsGold
    ? [{ key: 'subscription' }, { key: 'gold' }]
    : [{ key: 'subscription' }]
}

function normalizeGoodsSections(sections, supportsGold = true) {
  const supportedKeys = supportsGold ? ['subscription', 'gold'] : ['subscription']
  const normalized = []
  const appendKey = (key) => {
    if (supportedKeys.indexOf(key) === -1) return
    if (normalized.some((item) => item.key === key)) return
    normalized.push({ key })
  }

  if (Array.isArray(sections)) {
    sections.forEach((section) => {
      appendKey(typeof section === 'string' ? section : section && section.key)
    })
  }
  supportedKeys.forEach(appendKey)
  return normalized
}

function inferGoodsSections(payItems = [], supportsGold = true) {
  const orderedKeys = []
  payItems.forEach((item) => {
    const key = Number(item.goodsType) === 0 ? 'gold' : 'subscription'
    if (orderedKeys.indexOf(key) === -1) orderedKeys.push(key)
  })
  return normalizeGoodsSections(orderedKeys, supportsGold)
}

function createPanel(key, title, enabled, supportsGoods, supportsGold) {
  return {
    key,
    title,
    supportsGoods,
    supportsGold,
    thirdPay: enabled,
    payLinkType: 0,
    payMethods: [createDefaultPayMethod()],
    goodsSections: supportsGoods ? createGoodsSections(supportsGold) : [],
    subscriptionGoods: [],
    goldGoods: [],
    adTagGoodsId: null,
    adTagVisible: false,
    adTagPreviewGoodsId: null
  }
}

function createPlatformConfig(enabled) {
  return {
    panels: [
      createPanel('paywall', '付费墙', enabled, true, true),
      createPanel('wallet', '钱包页', enabled, true, true),
      createPanel('vip', 'VIP页', false, true, false)
    ],
    activity: {
      thirdPay: enabled,
      pushRuleIds: [],
      payMethods: [createDefaultPayMethod()]
    },
    discount: {
      thirdPay: enabled,
      payMethods: [createDefaultPayMethod()]
    }
  }
}

function normalizeOptionalNumber(value) {
  return value === 0 || (value !== null && value !== undefined && value !== '')
    ? Number(value)
    : ''
}

function normalizeGoodsId(value) {
  if (value === 0 || value === '0') return 0
  if (value === null || value === undefined || value === '') return null
  const numberValue = Number(value)
  return Number.isNaN(numberValue) ? value : numberValue
}

function normalizePayMethods(payMethods = []) {
  const list = Array.isArray(payMethods) && payMethods.length
    ? [...payMethods].sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
    : [createDefaultPayMethod()]

  const normalized = list.map((item) => {
    const payMethod = normalizeOptionalNumber(item.payMethod)
    return {
      id: ++payRowId,
      payMethod,
      provider: payMethod === 0 ? '' : normalizeOptionalNumber(item.provider),
      isDefault: !!item.isDefault,
      sortOrder: item.sortOrder
    }
  })

  if (!normalized.some((item) => item.isDefault) && normalized.length) {
    normalized[0].isDefault = true
  }

  return normalized
}

function normalizeSubscriptionGoods(list = []) {
  const source = Array.isArray(list) ? list : []

  return source.map((item) => ({
    id: ++goodsRowId,
    goodsType: Number(item.goodsType) === 2 ? 2 : 1,
    goodsId: normalizeGoodsId(item.goodsId || item.id),
    onlyThird: !!(item.onlyThirdParty || item.onlyThird),
    defaultPayMethod: normalizeOptionalNumber(item.defaultPayMethod),
    defaultProvider: normalizeOptionalNumber(item.defaultProvider),
    visible: false,
    previewGoodsId: normalizeGoodsId(item.previewGoodsId || item.goodsId)
  }))
}

function normalizeGoldGoods(list = []) {
  const source = Array.isArray(list) ? list : []

  return source.map((item) => ({
    id: ++goodsRowId,
    goodsType: 0,
    goodsId: normalizeGoodsId(item.goodsId || (item.isLocal ? null : item.id)),
    onlyThird: !!(item.onlyThirdParty || item.onlyThird),
    visible: false,
    previewGoodsId: normalizeGoodsId(item.previewGoodsId || item.goodsId)
  }))
}

function normalizePanel(source = {}, fallback = {}) {
  const supportsGoods =
    source.supportsGoods !== undefined ? !!source.supportsGoods : !!fallback.supportsGoods
  const supportsGold =
    source.supportsGold !== undefined ? !!source.supportsGold : !!fallback.supportsGold
  return {
    key: source.key || fallback.key,
    title: source.title || fallback.title,
    supportsGoods,
    supportsGold,
    thirdPay:
      source.thirdPay !== undefined ? !!source.thirdPay : !!fallback.thirdPay,
    payLinkType: Number(source.payLinkType || 0),
    payMethods: normalizePayMethods(source.payMethods || fallback.payMethods),
    goodsSections: supportsGoods
      ? normalizeGoodsSections(source.goodsSections || fallback.goodsSections, supportsGold)
      : [],
    subscriptionGoods: supportsGoods
      ? normalizeSubscriptionGoods(source.subscriptionGoods || fallback.subscriptionGoods)
      : [],
    goldGoods: supportsGold
      ? normalizeGoldGoods(source.goldGoods || fallback.goldGoods)
      : [],
    adTagGoodsId: normalizeGoodsId(source.adTagGoodsId),
    adTagVisible: false,
    adTagPreviewGoodsId: normalizeGoodsId(
      source.adTagPreviewGoodsId || source.adTagGoodsId
    )
  }
}

function normalizePlatformConfig(source = {}, enabled) {
  const fallback = createPlatformConfig(enabled)
  const sourcePanels = Array.isArray(source.panels) ? source.panels : []
  return {
    panels: fallback.panels.map((panel) => {
      const sourcePanel = sourcePanels.find((item) => item.key === panel.key)
      return normalizePanel(sourcePanel, panel)
    }),
    activity: {
      ...fallback.activity,
      ...(source.activity || {}),
      payMethods: normalizePayMethods(
        (source.activity && source.activity.payMethods) ||
          fallback.activity.payMethods
      )
    },
    discount: {
      ...fallback.discount,
      ...(source.discount || {}),
      payMethods: normalizePayMethods(
        (source.discount && source.discount.payMethods) ||
          fallback.discount.payMethods
      )
    }
  }
}

function getRuleByEntryId(rules, entryId) {
  return (rules || []).find((item) => Number(item.entryId) === entryId) || null
}

function normalizePanelFromRule(panel, rule) {
  if (!rule) return panel
  const payItems = Array.isArray(rule.payItems)
    ? [...rule.payItems].sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
    : []
  return normalizePanel(
    {
      ...panel,
      thirdPay: Number(rule.switchOn || 0) === 1,
      payLinkType: rule.payLinkType,
      payMethods: rule.payMethods,
      goodsSections: inferGoodsSections(payItems, panel.supportsGold),
      subscriptionGoods: payItems.filter((item) => Number(item.goodsType) !== 0),
      goldGoods: payItems.filter((item) => Number(item.goodsType) === 0),
      adTagGoodsId: rule.adUnlockItem && rule.adUnlockItem.goodsId
    },
    panel
  )
}

function normalizeSimpleRuleConfig(fallback, rule) {
  if (!rule) return fallback
  return {
    ...fallback,
    thirdPay: Number(rule.switchOn || 0) === 1,
    payLinkType: Number(rule.payLinkType || 0),
    payMethods: normalizePayMethods(rule.payMethods || fallback.payMethods),
    pushRuleIds: rule.pushRuleIds || fallback.pushRuleIds || []
  }
}

function normalizeRulesPlatformConfig(rules = [], enabled) {
  const fallback = createPlatformConfig(enabled)
  return {
    panels: fallback.panels.map((panel) => {
      return normalizePanelFromRule(
        panel,
        getRuleByEntryId(rules, PANEL_ENTRY_MAP[panel.key])
      )
    }),
    activity: normalizeSimpleRuleConfig(
      fallback.activity,
      getRuleByEntryId(rules, ACTIVITY_ENTRY_ID)
    ),
    discount: normalizeSimpleRuleConfig(
      fallback.discount,
      getRuleByEntryId(rules, DISCOUNT_ENTRY_ID)
    )
  }
}

function createForm(mode, editData) {
  const editing = mode !== 'add'
  const source = (editData && (editData.rechargeConfig || editData.config || editData)) || {}
  const templateId =
    source.templateId !== undefined && source.templateId !== null
      ? source.templateId
      : editData && editData.templateId !== undefined && editData.templateId !== null
        ? editData.templateId
        : ''
  const crowdType =
    source.crowdType !== undefined && source.crowdType !== null
      ? Number(source.crowdType)
      : -1
  return {
    id: editData && editData.id,
    templateId: templateId === 'default' ? '' : templateId,
    strategyName:
      source.strategyName || source.name || (editing && editData ? editData.strategyName : ''),
    crowdType,
    crowdIds: source.crowdIds || [],
    audienceType:
      source.audienceType || (crowdType === -1 ? '不限' : '选择人群'),
    ios: source.iosRules
      ? normalizeRulesPlatformConfig(source.iosRules, editing)
      : normalizePlatformConfig(source.ios, editing),
    android: source.androidRules
      ? normalizeRulesPlatformConfig(source.androidRules, editing)
      : normalizePlatformConfig(source.android, editing)
  }
}

export default {
  name: 'RechargeTemplateEditor',
  components: {
    draggable
  },
  props: {
    mode: {
      type: String,
      default: 'add'
    },
    editData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      activePlatform: 'ios',
      saving: false,
      crowdOptions: [],
      activityOptions: [],
      templateOptions: [],
      selectedTemplateOption: null,
      templateSearchLoading: false,
      templateDetailLoading: false,
      templateSearchSeq: 0,
      maxMethodCount: 6,
      iosMethodOptions: [
        { dictValue: '0', dictName: '内购' },
        { dictValue: '1', dictName: '卡支付' },
        { dictValue: '2', dictName: 'Apple Pay' },
        { dictValue: '4', dictName: '钱包' }
      ],
      androidMethodOptions: [
        { dictValue: '0', dictName: '内购' },
        { dictValue: '1', dictName: '卡支付' },
        { dictValue: '3', dictName: 'Google Pay' },
        { dictValue: '4', dictName: '钱包' }
      ],
      providerOptions: [
        { dictValue: '0', dictName: 'Stripe' },
        { dictValue: '1', dictName: 'Antom' },
        { dictValue: '2', dictName: 'PayPal' }
      ],
      goodsOptions: {
        ios: {
          vip: [],
          pass: [],
          gold: []
        },
        android: {
          vip: [],
          pass: [],
          gold: []
        }
      },
      form: createForm(this.mode, this.editData)
    }
  },
  computed: {
    currentConfig() {
      return this.form[this.activePlatform]
    },
    pageTitle() {
      if (this.mode === 'view') return '查看策略'
      return this.mode === 'edit' ? '编辑策略' : '新增策略-默认'
    },
    readonly() {
      return this.mode === 'view'
    }
  },
  mounted() {
    this.loadGoodsOptions()
    this.fetchCrowdOptions()
    this.fetchActivityOptions()
  },
  methods: {
    getResponseList(response) {
      const data = response && response.data
      if (Array.isArray(data)) return data
      return data && Array.isArray(data.records) ? data.records : []
    },
    normalizeActivityPushRuleOption(item = {}) {
      const label = item.ruleName || item.activityName || item.name || ''
      return {
        id: item.id,
        label: label || String(item.id || ''),
        raw: item
      }
    },
    normalizeTemplateOption(item = {}) {
      const label =
        item.name ||
        item.strategyName ||
        item.templateName ||
        item.configName ||
        ''
      return {
        value: item.id,
        label: label || String(item.id || ''),
        raw: item
      }
    },
    setTemplateOptions(records = []) {
      const options = records
        .filter((item) => item && item.id !== undefined && item.id !== null)
        .map((item) => this.normalizeTemplateOption(item))
      const selectedValue = this.form && this.form.templateId

      if (
        this.selectedTemplateOption &&
        selectedValue &&
        !options.some((item) => String(item.value) === String(selectedValue))
      ) {
        options.unshift(this.selectedTemplateOption)
      }

      this.templateOptions = options
    },
    searchTemplateOptions: debounce(async function (query = '') {
      const keyword = String(query || '').trim()
      if (!keyword) {
        this.templateOptions = this.selectedTemplateOption
          ? [this.selectedTemplateOption]
          : []
        return
      }

      const searchSeq = ++this.templateSearchSeq
      this.templateSearchLoading = true
      try {
        const response = await getRechargeTemplateConfigPage({
          current: 1,
          size: 10000,
          name: keyword
        })
        if (searchSeq !== this.templateSearchSeq) return
        this.setTemplateOptions(this.getResponseList(response))
      } catch (error) {
        if (searchSeq === this.templateSearchSeq) {
          this.templateOptions = this.selectedTemplateOption
            ? [this.selectedTemplateOption]
            : []
        }
        this.$Message.error('搜索模板失败')
      } finally {
        if (searchSeq === this.templateSearchSeq) {
          this.templateSearchLoading = false
        }
      }
    }, 500),
    applyTemplateConfig(detail = {}) {
      const source =
        (detail && (detail.rechargeConfig || detail.config || detail)) || {}
      const iosConfig = source.iosRules
        ? normalizeRulesPlatformConfig(source.iosRules, true)
        : normalizePlatformConfig(source.ios, true)
      const androidConfig = source.androidRules
        ? normalizeRulesPlatformConfig(source.androidRules, true)
        : normalizePlatformConfig(source.android, true)

      this.$set(this.form, 'ios', iosConfig)
      this.$set(this.form, 'android', androidConfig)
      this.activePlatform = 'ios'
    },
    async handleTemplateChange(value) {
      if (!value) {
        this.selectedTemplateOption = null
        this.templateOptions = []
        return
      }

      const selectedOption = this.templateOptions.find(
        (item) => String(item.value) === String(value)
      )
      if (selectedOption) this.selectedTemplateOption = selectedOption

      this.templateDetailLoading = true
      try {
        const { data } = await getRechargeTemplateConfigInfo(value)
        const detail = data || {}
        this.selectedTemplateOption = this.normalizeTemplateOption({
          ...detail,
          id: value
        })
        this.templateOptions = [this.selectedTemplateOption]
        this.applyTemplateConfig(detail)
      } catch (error) {
        this.$Message.error('获取模板详情失败')
      } finally {
        this.templateDetailLoading = false
      }
    },
    buildGoodsClassificationParams(platform) {
      return {
        channel: PLATFORM_CHANNEL_MAP[platform],
        goodsVersion: 1
      }
    },
    getClassificationData(response) {
      const data = response && response.data
      return data && !Array.isArray(data) ? data : {}
    },
    normalizeClassificationGoodsList(list = [], goodsType) {
      const source = Array.isArray(list) ? list : []
      return source.map((item) => ({
        ...item,
        id: normalizeGoodsId(item.id),
        goodsType:
          item.goodsType === undefined || item.goodsType === null
            ? goodsType
            : Number(item.goodsType)
      }))
    },
    async loadGoodsOptions() {
      try {
        const [
          iosClassification,
          androidClassification
        ] = await Promise.all([
          getGoodsClassification(this.buildGoodsClassificationParams('ios')),
          getGoodsClassification(this.buildGoodsClassificationParams('android'))
        ])
        const iosGoods = this.getClassificationData(iosClassification)
        const androidGoods = this.getClassificationData(androidClassification)

        this.goodsOptions = {
          ios: {
            vip: this.normalizeClassificationGoodsList(iosGoods.vipList, 1),
            pass: this.normalizeClassificationGoodsList(iosGoods.passList, 2),
            gold: this.normalizeClassificationGoodsList(iosGoods.coinsList, 0)
          },
          android: {
            vip: this.normalizeClassificationGoodsList(androidGoods.vipList, 1),
            pass: this.normalizeClassificationGoodsList(androidGoods.passList, 2),
            gold: this.normalizeClassificationGoodsList(androidGoods.coinsList, 0)
          }
        }
      } catch (error) {
        this.$Message.error('获取付费项列表失败')
      }
    },
    async fetchCrowdOptions() {
      try {
        const response = await getCrowdList({ current: 1, size: 100000 })
        this.crowdOptions = this.getResponseList(response)
      } catch (error) {
        this.crowdOptions = []
        this.$Message.error('获取人群包失败')
      }
    },
    async fetchActivityOptions() {
      try {
        const response = await getActivityPushRuleList({
          current: 1,
          size: 10000
        })
        this.activityOptions = this.getResponseList(response)
          .filter((item) => item && item.id !== undefined && item.id !== null)
          .map((item) => this.normalizeActivityPushRuleOption(item))
      } catch (error) {
        this.activityOptions = []
        this.$Message.error('获取活动推送配置列表失败')
      }
    },
    handleAudienceTypeChange(value) {
      if (value === '不限') {
        this.$set(this.form, 'crowdType', -1)
        this.$set(this.form, 'crowdIds', [])
        return
      }

      this.$set(this.form, 'crowdType', 0)
      if (!Array.isArray(this.form.crowdIds)) {
        this.$set(this.form, 'crowdIds', [])
      }
    },
    getMethodOptions(platform) {
      return platform === 'ios'
        ? this.iosMethodOptions
        : this.androidMethodOptions
    },
    addPayMethod(panel) {
      if (panel.payMethods.length >= this.maxMethodCount) {
        this.$Message.warning('最多添加6个支付方式')
        return
      }
      panel.payMethods.push(createExtraPayMethod())
    },
    removePayMethod(panel, index) {
      const list = panel.payMethods
      if (list[index] && list[index].payMethod === 0) {
        this.$Message.warning('内购支付方式不可删除')
        return
      }

      const wasDefault = list[index] && list[index].isDefault
      list.splice(index, 1)
      if (wasDefault && list.length) {
        this.$set(list[0], 'isDefault', true)
      }
    },
    setDefaultPayMethod(panel, index) {
      panel.payMethods.forEach((item, itemIndex) => {
        this.$set(item, 'isDefault', itemIndex === index)
      })
    },
    isMethodUsed(panel, value, currentIndex) {
      return panel.payMethods.some((item, index) => {
        return index !== currentIndex && item.payMethod === value
      })
    },
    onMethodChange(panel, index, value) {
      const list = panel.payMethods
      const row = list[index]
      if (!row) return

      if (value === 0) {
        this.$set(row, 'provider', '')
      } else {
        if (this.isWalletPayMethod(value)) {
          const paypalOption = this.providerOptions.find(
            (item) => item.dictName === 'PayPal'
          )
          if (paypalOption) {
            this.$set(row, 'provider', Number(paypalOption.dictValue))
          }
        } else if (
          this.isAppleOrGooglePayMethod(value) &&
          this.providerOptions.some(
            (item) =>
              item.dictName === 'PayPal' &&
              row.provider === Number(item.dictValue)
          )
        ) {
          this.$set(row, 'provider', '')
        }
      }

      if (!list.some((item) => item.isDefault)) {
        this.$set(row, 'isDefault', true)
      }
    },
    onDefaultPayMethodChange(row, value) {
      if (this.isWalletPayMethod(value)) {
        const paypalOption = this.providerOptions.find(
          (item) => item.dictName === 'PayPal'
        )
        this.$set(
          row,
          'defaultProvider',
          paypalOption ? Number(paypalOption.dictValue) : ''
        )
        return
      }
      const options = this.getDefaultProviderOptions(value)
      const currentAvailable = options.some(
        (item) => String(item.dictValue) === String(row.defaultProvider)
      )
      if (!currentAvailable) {
        this.$set(row, 'defaultProvider', '')
      }
    },
    isWalletPayMethod(value) {
      const walletOption = this.getMethodOptions(this.activePlatform).find(
        (item) => item.dictName === '钱包'
      )
      return !!walletOption && value === Number(walletOption.dictValue)
    },
    isAppleOrGooglePayMethod(value) {
      const targetOption = this.getMethodOptions(this.activePlatform).find(
        (item) => item.dictName === 'Apple Pay' || item.dictName === 'Google Pay'
      )
      return !!targetOption && value === Number(targetOption.dictValue)
    },
    isWalletSelected(panel, index) {
      const row = panel.payMethods[index]
      return !!row && this.isWalletPayMethod(row.payMethod)
    },
    isAppleOrGooglePaySelected(panel, index) {
      const row = panel.payMethods[index]
      return !!row && this.isAppleOrGooglePayMethod(row.payMethod)
    },
    getDefaultProviderOptions(payMethod) {
      if (this.isAppleOrGooglePayMethod(payMethod)) {
        return this.providerOptions.filter((item) => item.dictName !== 'PayPal')
      }
      if (this.isWalletPayMethod(payMethod)) {
        return this.providerOptions.filter((item) => item.dictName === 'PayPal')
      }
      return this.providerOptions
    },
    getFilteredProviders(panel, index) {
      if (this.isAppleOrGooglePaySelected(panel, index)) {
        return this.providerOptions.filter((item) => item.dictName !== 'PayPal')
      }
      if (this.isWalletSelected(panel, index)) {
        return this.providerOptions.filter((item) => item.dictName === 'PayPal')
      }
      return this.providerOptions
    },
    getPanelGoodsSections(panel) {
      if (!panel || !panel.supportsGoods) return []
      const sections = normalizeGoodsSections(panel.goodsSections, panel.supportsGold)
      const current = Array.isArray(panel.goodsSections) ? panel.goodsSections : []
      const changed =
        sections.length !== current.length ||
        sections.some((section, index) => {
          return !current[index] || current[index].key !== section.key
        })
      if (changed) {
        this.$set(panel, 'goodsSections', sections)
        return sections
      }
      return panel.goodsSections
    },
    addSubscriptionGoods(panel) {
      panel.subscriptionGoods.push(createSubscriptionGoods())
    },
    addGoldGoods(panel) {
      panel.goldGoods.push(...createGoldGoodsRow())
    },
    removeGoldGoodsRow(panel, index) {
      const rowStartIndex = Math.floor(index / 2) * 2
      panel.goldGoods.splice(rowStartIndex, 2)
    },
    removeGoodsRow(list, index) {
      list.splice(index, 1)
    },
    setSubscriptionType(row, goodsType) {
      if (row.goodsType === goodsType) return
      this.$set(row, 'goodsType', goodsType)
      this.$set(row, 'goodsId', null)
      this.$set(row, 'previewGoodsId', null)
    },
    selectSubscriptionGoods(row, item) {
      this.$set(row, 'goodsId', item.id)
      this.$set(row, 'previewGoodsId', item.id)
      this.$set(row, 'visible', false)
    },
    selectGoldGoods(row, item) {
      this.$set(row, 'goodsId', item.id)
      this.$set(row, 'previewGoodsId', item.id)
      this.$set(row, 'visible', false)
    },
    selectAdTagGoods(panel, item) {
      this.$set(panel, 'adTagGoodsId', item.id)
      this.$set(panel, 'adTagPreviewGoodsId', item.id)
      this.$set(panel, 'adTagVisible', false)
    },
    clearAdTagGoods(panel) {
      this.$set(panel, 'adTagGoodsId', null)
      this.$set(panel, 'adTagPreviewGoodsId', null)
    },
    setPreviewGoods(row, goodsId) {
      this.$set(row, 'previewGoodsId', goodsId)
    },
    setGoldPreviewGoods(row, goodsId) {
      this.$set(row, 'previewGoodsId', goodsId)
    },
    setAdTagPreviewGoods(panel, goodsId) {
      this.$set(panel, 'adTagPreviewGoodsId', goodsId)
    },
    getGoodsOptions(platform, type) {
      return this.goodsOptions[platform] && this.goodsOptions[platform][type]
        ? this.goodsOptions[platform][type]
        : []
    },
    getSubscriptionOptions(platform, row) {
      return this.getGoodsOptions(platform, row.goodsType === 2 ? 'pass' : 'vip')
    },
    isSameGoodsId(left, right) {
      if (
        left === undefined ||
        left === null ||
        right === undefined ||
        right === null
      ) {
        return false
      }
      return String(left) === String(right)
    },
    getGoodsById(platform, goodsType, goodsId) {
      const type = goodsType === 0 ? 'gold' : goodsType === 2 ? 'pass' : 'vip'
      return this.getGoodsOptions(platform, type).find(
        (item) => this.isSameGoodsId(item.id, goodsId)
      )
    },
    getPreviewGoods(row, platform) {
      return this.getGoodsById(
        platform,
        row.goodsType,
        row.previewGoodsId || row.goodsId
      )
    },
    getGoldPickerPreview(row, platform) {
      return this.getGoodsById(platform, 0, row.previewGoodsId || row.goodsId)
    },
    getGoodsSelectLabel(row, platform) {
      const item = this.getGoodsById(platform, row.goodsType, row.goodsId)
      if (!item) return '— 选择付费项 —'
      return `${row.goodsType === 2 ? '通行证' : 'VIP'} - ${this.formatGoodsLabel(
        item
      )}`
    },
    getGoodsTriggerText(row, platform) {
      const item = this.getGoodsById(platform, row.goodsType, row.goodsId)
      return item ? item.goodsName : '— 选择付费项 —'
    },
    getGoldTriggerText(row, platform) {
      const item = this.getGoldPreview(row, platform)
      return item ? item.goodsName : '— 选择金币项 —'
    },
    getSubscriptionTipGoods(row, platform) {
      return this.getGoodsById(platform, row.goodsType, row.goodsId)
    },
    getThirdMethodOptions(platform) {
      return this.getMethodOptions(platform).filter(
        (item) => Number(item.dictValue) !== 0
      )
    },
    getAdTagGoodsOptions(platform) {
      return this.getGoodsOptions(platform, 'vip')
    },
    getAdTagSelectedGoods(panel, platform) {
      return this.getGoodsById(platform, 1, panel.adTagGoodsId)
    },
    getAdTagPickerPreview(panel, platform) {
      return this.getGoodsById(
        platform,
        1,
        panel.adTagPreviewGoodsId || panel.adTagGoodsId
      )
    },
    getAdTagTriggerText(panel, platform) {
      const item = this.getAdTagSelectedGoods(panel, platform)
      return item ? item.goodsName : '— 选择订阅付费项 —'
    },
    getGoldPreview(row, platform) {
      return this.getGoodsById(platform, 0, row.goodsId)
    },
    getSubscriptionTipRows(item = {}, platform) {
      if (!item.id) return []
      const rows = [
        {
          key: 'duration',
          label: '时长',
          value: this.formatGoodsDuration(item, platform)
        },
        {
          key: 'inner-price',
          label: '内购价',
          value: this.formatGoodsPrice(item)
        }
      ]
      rows.push(...this.getDiscountPreview(item))
      if (Number(item.goodsType) === 2) {
        rows.push(...this.getRewardPreview(item))
      }
      return rows
    },
    formatGoldCount(item = {}) {
      if (item.goodsNum !== undefined && item.goodsNum !== null) {
        return item.goodsNum
      }
      return '--'
    },
    getRewardPreview(item = {}) {
      return [
        {
          key: 'reward-inner',
          label: '内购赠币',
          rawValue: this.getRewardValue(item, 'iapReward', 'iapReward'),
          className: 'green-text'
        },
        {
          key: 'reward-stripe',
          label: 'Stripe赠币',
          rawValue: this.getRewardValue(item, 'stripeReward', 'stripeReward'),
          className: 'blue-text'
        },
        {
          key: 'reward-antom',
          label: 'Antom赠币',
          rawValue: this.getRewardValue(item, 'antomReward', 'antomReward'),
          className: 'blue-text'
        },
        {
          key: 'reward-paypal',
          label: 'PayPal赠币',
          rawValue: this.getRewardValue(item, 'paypalReward', 'paypalReward'),
          className: 'blue-text'
        }
      ]
        .filter((row) => this.hasRewardValue(row.rawValue))
        .map((row) => ({
          ...row,
          value: row.rawValue
        }))
    },
    getGoldTipRows(item = {}) {
      if (!item.id) return []
      return [
        {
          key: 'gold',
          label: '金币数',
          value: this.formatGoldCount(item)
        },
        {
          key: 'inner-price',
          label: '内购价',
          value: this.formatGoodsPrice(item)
        },
        ...this.getDiscountPreview(item),
        ...this.getRewardPreview(item)
      ]
    },
    formatGoodsLabel(item = {}) {
      const price =
        item.goodsPrice !== undefined && item.goodsPrice !== null
          ? item.goodsPrice
          : '--'
      const priceParts = []
      if (item.goodsChannelName) priceParts.push(item.goodsChannelName)
      priceParts.push(price)
      if (item.goodsNum !== undefined && item.goodsNum !== null) {
        priceParts.push(item.goodsNum)
      }
      return `${priceParts.join('-')}(${item.goodsName || '--'})`
    },
    formatGoodsPrice(item = {}) {
      if (item.goodsPrice === undefined || item.goodsPrice === null) return '--'
      return `$${item.goodsPrice}`
    },
    formatGoodsDuration(item = {}, platform) {
      if (item.vipExpireTime) {
        if (platform === 'android' && Number(item.goodsType) === 1) {
          return VIP_DURATION_MAP[item.vipExpireTime - 1] || item.vipExpireTime
        }
        return COMMON_DURATION_MAP[item.vipExpireTime] || `${item.vipExpireTime}天`
      }
      if (item.goodsDay) return `${item.goodsDay}天`
      if (item.goodsNum && item.goodsType === 2) return `${item.goodsNum}天`
      return '--'
    },
    formatDiscountValue(config) {
      if (!config) return '--'
      if (typeof config !== 'object') return config
      if (config.price !== undefined && config.price !== null) {
        return `$${config.price}`
      }
      if (config.discount !== undefined && config.discount !== null) {
        return config.discount
      }
      return '--'
    },
    hasDisplayValue(value) {
      return value !== undefined && value !== null && value !== ''
    },
    hasRewardValue(value) {
      return this.hasDisplayValue(value) && Number(value) > 0
    },
    getRewardValue(item = {}, detailKey, fallbackKey) {
      const rewardDetail = item.rewardDetail || {}
      return this.hasDisplayValue(rewardDetail[detailKey])
        ? rewardDetail[detailKey]
        : item[fallbackKey]
    },
    getDiscountConfigFromPriceDetail(priceDetail, key) {
      if (
        !priceDetail ||
        priceDetail[key] === undefined ||
        priceDetail[key] === null
      ) {
        return null
      }
      return { price: priceDetail[key] }
    },
    getInnerDiscountValue(item = {}) {
      const config = this.getDiscountConfigFromPriceDetail(
        item.priceDetail,
        'iapPrice'
      )
      if (config) return this.formatDiscountValue(config)
      if (this.hasDisplayValue(item.discountGoodsPrice)) {
        return `$${item.discountGoodsPrice}`
      }
      if (this.hasDisplayValue(item.discount)) {
        return this.formatDiscountValue(item.discount)
      }
      return null
    },
    getDiscountPreview(item = {}) {
      const thirdMap = item.thirdPaymentDiscountMap || {}
      const priceDetail = item.priceDetail || {}
      return [
        {
          key: 'discount-inner',
          label: '内购折扣价',
          value: this.getInnerDiscountValue(item),
          className: 'green-text'
        },
        {
          key: 'discount-stripe',
          label: 'Stripe折扣价',
          value: this.formatDiscountValue(
            thirdMap.stripe ||
              this.getDiscountConfigFromPriceDetail(priceDetail, 'stripePrice')
          ),
          className: 'blue-text'
        },
        {
          key: 'discount-antom',
          label: 'Antom折扣价',
          value: this.formatDiscountValue(
            thirdMap.antom ||
              this.getDiscountConfigFromPriceDetail(priceDetail, 'antomPrice')
          ),
          className: 'blue-text'
        },
        {
          key: 'discount-paypal',
          label: 'PayPal折扣价',
          value: this.formatDiscountValue(
            thirdMap.paypal ||
              this.getDiscountConfigFromPriceDetail(priceDetail, 'paypalPrice')
          ),
          className: 'blue-text'
        }
      ].filter((row) => row.value !== '--' && this.hasDisplayValue(row.value))
    },
    hasSelectedGoods(item) {
      return !!(
        item &&
        (item.goodsId === 0 ||
          (item.goodsId !== undefined && item.goodsId !== null && item.goodsId !== ''))
      )
    },
    isSubscriptionGoodsUsed(list, goodsType, goodsId, currentIndex) {
      return list.some((item, index) => {
        return (
          index !== currentIndex &&
          Number(item.goodsType) === Number(goodsType) &&
          this.hasSelectedGoods(item) &&
          this.isSameGoodsId(item.goodsId, goodsId)
        )
      })
    },
    isGoodsUsed(list, goodsId, currentIndex) {
      return list.some((item, index) => {
        return (
          index !== currentIndex &&
          this.hasSelectedGoods(item) &&
          this.isSameGoodsId(item.goodsId, goodsId)
        )
      })
    },
    isStepThirdPay(panel) {
      return !!(panel && Number(panel.payLinkType) === 1)
    },
    validatePayMethods(panel, label) {
      if (!panel.thirdPay) return true
      if (!panel.payMethods.length) {
        this.$Message.warning(`请至少配置一种${label}支付方式`)
        return false
      }

      let hasDefault = false
      for (const item of panel.payMethods) {
        if (item.payMethod !== 0 && item.payMethod === '') {
          this.$Message.warning(`请选择${label}支付方式`)
          return false
        }
        if (item.payMethod !== 0 && item.provider === '') {
          this.$Message.warning(`请选择${label}支付服务商`)
          return false
        }
        if (item.isDefault) hasDefault = true
      }

      if (!hasDefault && panel.payMethods.length) {
        this.$set(panel.payMethods[0], 'isDefault', true)
      }
      return true
    },
    validateGoodsDuplicates(panel, label) {
      if (!panel.supportsGoods) return true
      const selectedMap = new Map()
      const validateList = (list = []) => {
        return list.every((item) => {
          if (!this.hasSelectedGoods(item)) return true
          const key = `${Number(item.goodsType)}-${String(item.goodsId)}`
          if (selectedMap.has(key)) {
            this.$Message.warning(`${label}付费项不能重复`)
            return false
          }
          selectedMap.set(key, true)
          return true
        })
      }
      if (!validateList(panel.subscriptionGoods)) return false
      if (panel.supportsGold && !validateList(panel.goldGoods)) return false
      return true
    },
    validatePanelConfigs() {
      const platformLabels = {
        ios: 'iOS',
        android: '安卓'
      }
      return ['ios', 'android'].every((platform) => {
        const panelValid = this.form[platform].panels.every((panel) => {
          const label = `${platformLabels[platform]}${panel.title}`
          return (
            this.validatePayMethods(panel, label) &&
            this.validateGoodsDuplicates(panel, label)
          )
        })
        if (!panelValid) return false
        if (
          !this.validatePayMethods(
            this.form[platform].activity,
            `${platformLabels[platform]}活动H5`
          )
        ) {
          return false
        }
        return this.validatePayMethods(
          this.form[platform].discount,
          `${platformLabels[platform]}折扣弹窗`
        )
      })
    },
    buildPayMethodPayload(payMethods = []) {
      return payMethods
        .filter((item) => item.payMethod === 0 || item.payMethod !== '')
        .map((item, index) => {
          const payload = {
            payMethod: Number(item.payMethod),
            isDefault: !!item.isDefault,
            sortOrder: index + 1
          }
          if (item.payMethod !== 0 && (item.provider === 0 || item.provider !== '')) {
            payload.provider = Number(item.provider)
          }
          return payload
        })
    },
    buildGoodsPayload(list = [], forceGoodsType) {
      return list
        .filter((item) => item.goodsId)
        .map((item) => {
          const goodsType =
            forceGoodsType === undefined ? Number(item.goodsType) : forceGoodsType
          const payload = {
            goodsId: item.goodsId,
            goodsType,
            onlyThirdParty: !!item.onlyThird
          }
          if (item.onlyThird) {
            if (item.defaultPayMethod === 0 || item.defaultPayMethod !== '') {
              payload.defaultPayMethod = Number(item.defaultPayMethod)
            }
            if (item.defaultProvider === 0 || item.defaultProvider !== '') {
              payload.defaultProvider = Number(item.defaultProvider)
            }
          }
          return payload
        })
    },
    buildPanelGoodsPayload(panel) {
      if (!panel.supportsGoods) return []
      const payItems = []
      this.getPanelGoodsSections(panel).forEach((section) => {
        if (section.key === 'subscription') {
          payItems.push(...this.buildGoodsPayload(panel.subscriptionGoods))
        }
        if (section.key === 'gold' && panel.supportsGold) {
          payItems.push(...this.buildGoodsPayload(panel.goldGoods, 0))
        }
      })
      return payItems.map((item, index) => ({
        ...item,
        sortOrder: index + 1
      }))
    },
    buildPanelRule(panel) {
      return {
        entryId: PANEL_ENTRY_MAP[panel.key],
        switchOn: panel.thirdPay ? 1 : 0,
        payLinkType: Number(panel.payLinkType || 0),
        payMethods: this.buildPayMethodPayload(panel.payMethods),
        payItems: this.buildPanelGoodsPayload(panel),
        adUnlockItem: panel.key === 'paywall' && panel.adTagGoodsId
          ? { goodsId: panel.adTagGoodsId }
          : null
      }
    },
    buildSimpleRule(entryId, config) {
      return {
        entryId,
        switchOn: config.thirdPay ? 1 : 0,
        payLinkType: Number(config.payLinkType || 0),
        payMethods: this.buildPayMethodPayload(config.payMethods),
        payItems: [],
        pushRuleIds: config.pushRuleIds || []
      }
    },
    buildPlatformRules(platform) {
      const config = this.form[platform]
      return [
        ...config.panels.map((panel) => this.buildPanelRule(panel)),
        this.buildSimpleRule(DISCOUNT_ENTRY_ID, config.discount),
        this.buildSimpleRule(ACTIVITY_ENTRY_ID, config.activity)
      ]
    },
    buildSubmitPayload() {
      const crowdType = this.form.audienceType === '不限'
        ? -1
        : Number(this.form.crowdType === -1 ? 0 : this.form.crowdType)
      return {
        id: this.form.id,
        name: this.form.strategyName.trim(),
        crowdType,
        crowdIds: crowdType === -1 ? [] : this.form.crowdIds,
        iosRules: this.buildPlatformRules('ios'),
        androidRules: this.buildPlatformRules('android')
      }
    },
    handleBack() {
      this.$emit('back')
    },
    handleSave() {
      this.$refs.formRef.validate((valid) => {
        if (!valid) return
        if (!this.validatePanelConfigs()) return
        this.saving = true
        this.$emit('save', this.buildSubmitPayload(), () => {
          this.saving = false
        })
      })
    }
  }
}
</script>

<style scoped lang="less">
.editor-page {
  position: relative;
  height: 100%;
  padding: 16px;
  background: #fff;
  overflow: auto;
}

.editor-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.back-button {
  width: auto;
  height: 20px;
  padding: 0;
  margin-right: 8px;
  color: #17233d;
  font-size: 14px;
  line-height: 20px;
  border: 0;
}

.editor-title {
  color: #17233d;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.base-card {
  margin-bottom: 10px;
  background: #fff;
}

.form-line {
  display: flex;
  align-items: center;
  min-height: 36px;

  /deep/ .ivu-form-item {
    margin-right: 20px;
    margin-bottom: 0;
  }
}

.template-select {
  width: 180px;
}

.strategy-input {
  width: 180px;
}

.form-tip {
  color: #999;
  font-size: 12px;
}

.crowd-select {
  width: 300px;
}

.crowd-preview-card {
  white-space: normal;
}

.crowd-preview-title {
  padding-bottom: 8px;
  margin-bottom: 10px;
  color: #17233d;
  font-size: 14px;
  font-weight: 600;
  border-bottom: 1px solid #e8eaec;
}

.crowd-preview-body {
  font-size: 13px;
}

.crowd-preview-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 6px;
  line-height: 1.5;
}

.crowd-preview-label {
  min-width: 70px;
  color: #808695;
  flex-shrink: 0;

  &::after {
    content: '：';
  }
}

.crowd-preview-value {
  color: #515a6e;
  word-break: break-all;
}

.crowd-country-text-wrapper {
  min-width: 0;
  line-height: 1.5;
  flex: 1;

  .ellipsis-text {
    display: block;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.config-card {
  background: #fff;
}

.section-title {
  margin: 12px 0 10px;
  color: #333;
  font-size: 14px;
  font-weight: 600;
}

.payment-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(280px, 1fr));
  gap: 14px;
}

.payment-card {
  min-height: 250px;
  padding: 0 10px 10px;
  border: 1px solid #e8eaec;
  border-radius: 4px;
}

.payment-title {
  height: 32px;
  margin: 0 -10px 8px;
  padding: 8px 12px;
  color: #333;
  font-size: 12px;
  font-weight: 600;
  background: #fbfbfc;
  border-bottom: 1px solid #e8eaec;
}

.switch-box {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 8px;
  color: #333;
  font-size: 12px;
  background: #fafafa;
  border: 1px solid #e8eaec;
  border-radius: 4px;
}

.radio-row {
  display: flex;
  align-items: center;
  height: 30px;
  font-size: 12px;
}

.table-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
  color: #333;
  font-size: 12px;
}

.method-table,
.simple-table {
  margin-top: 4px;
  border: 1px solid #edf0f2;
}

.method-head,
.method-row {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) minmax(0, 1fr) 40px 20px;
  align-items: center;
  min-height: 32px;
  column-gap: 6px;
  padding: 0 8px;
}

.method-head {
  color: #666;
  font-size: 12px;
  background: #fafafa;
  border-bottom: 1px solid #edf0f2;

  span:nth-child(4) {
    text-align: center;
  }
}

.method-row {
  padding-top: 3px;
  padding-bottom: 3px;
  border-bottom: 1px solid #f2f3f5;

  &:last-child {
    border-bottom: 0;
  }

  /deep/ .ivu-select {
    width: 100%;
  }

  /deep/ .ivu-radio-wrapper {
    margin-right: 0;
    justify-self: center;
  }

  .row-delete {
    justify-self: center;
  }
}

.simple-table {
  .method-head,
  .method-row {
    grid-template-columns: 36px minmax(140px, 1fr) minmax(140px, 1fr) 90px 24px;
  }
}

.drag-handle {
  color: #bbb;
  text-align: center;
  cursor: move;
}

.row-delete {
  color: #ff4d4f;
  text-align: center;
}

.add-row {
  display: flex;
  align-items: center;
  min-height: 28px;
  margin-top: 6px;
  padding: 0 8px;
  color: #666;
  font-size: 12px;
  background: #fafafa;
  border: 1px solid #edf0f2;
  border-radius: 2px;
}

.add-row {
  justify-content: center;
  color: #999;
  background: #fff;
  border-style: dashed;
}

.goods-config-table {
  margin-top: 8px;
}

.goods-group-title {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  margin-top: 8px;
  padding: 0 8px;
  color: #333;
  font-size: 12px;
  font-weight: 600;
  background: #fafafa;
  border: 1px solid #edf0f2;
  border-radius: 2px;
}

.group-handle {
  font-weight: 400;
}

.subscription-item {
  margin-top: 6px;
  padding: 8px;
  border: 1px solid #edf0f2;
  border-radius: 4px;
}

.subscription-main {
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr) 64px 16px;
  align-items: center;
  column-gap: 8px;

  /deep/ .ivu-select {
    width: 100%;
  }

  .row-delete {
    justify-self: center;
  }

  &.without-third {
    grid-template-columns: 16px minmax(0, 1fr) 16px;
  }
}

.subscription-default {
  margin-top: 8px;
  padding: 10px;
  background: #fafafa;
  border-radius: 4px;
}

.step-pay-tip {
  padding: 6px 8px;
  color: #808695;
  font-size: 12px;
  line-height: 18px;
  background: #f7fbfa;
  border: 1px solid #dcdee2;
  border-radius: 4px;
}

.default-line {
  display: flex;
  align-items: center;
  font-size: 12px;

  label {
    width: 84px;
    color: #515a6e;
    flex-shrink: 0;
  }

  .inline-label {
    width: auto;
    margin-left: 16px;
  }

  /deep/ .ivu-select {
    width: 120px;
  }
}

.gold-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 6px;
}

.gold-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 16px;
  align-items: center;
  column-gap: 6px;

  /deep/ .ivu-select {
    width: 100%;
  }

  .row-delete {
    justify-self: center;
  }
}

.goods-picker-poptip {
  width: 100%;

  /deep/ .ivu-poptip-rel {
    width: 100%;
  }
}

.goods-picker-trigger-wrap {
  position: relative;
  width: 100%;
}

.goods-mini-preview {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  z-index: 20;
  display: none;
  width: 200px;
  padding: 10px 12px;
  color: #515a6e;
  font-size: 12px;
  white-space: normal;
  pointer-events: none;
  background: #fff;
  border: 1px solid #dcdee2;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transform: translateX(-50%);
}

.goods-picker-trigger-wrap:hover .goods-mini-preview {
  display: block;
}

.goods-mini-title {
  margin-bottom: 6px;
  color: #333;
  font-size: 14px;
  font-weight: 600;
  word-break: break-all;
}

.goods-mini-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 22px;
  color: #999;

  strong {
    min-width: 56px;
    color: #515a6e;
    text-align: right;
    word-break: break-all;
  }
}

.goods-picker-trigger {
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 24px 0 8px;
  color: #515a6e;
  white-space: nowrap;
  overflow: hidden;
  background: #fff;
  border: 1px solid #dcdee2;
  border-radius: 4px;
  cursor: pointer;

  .trigger-text {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 8px;
    width: 0;
    height: 0;
    margin-top: -2px;
    border: 4px solid transparent;
    border-top-color: #808695;
  }

  &.disabled {
    color: #c5c8ce;
    background: #f3f3f3;
    cursor: not-allowed;
  }
}

.subscription-picker {
  width: 560px;
  color: #515a6e;
  white-space: normal;
}

.picker-tabs {
  display: flex;
  align-items: center;
  height: 32px;
  padding: 0 12px;
  border-bottom: 1px solid #edf0f2;

  span {
    height: 32px;
    margin-right: 22px;
    color: #515a6e;
    font-weight: 600;
    line-height: 32px;
    cursor: pointer;
    border-bottom: 2px solid transparent;
  }

  .active {
    color: #2d8cf0;
    border-bottom-color: #2d8cf0;
  }
}

.picker-content {
  display: grid;
  grid-template-columns: minmax(0, 280px) minmax(0, 1fr);
  height: 250px;
  overflow: hidden;
}

.picker-list {
  min-height: 0;
  padding: 10px 12px;
  overflow-x: hidden;
  overflow-y: auto;
  border-right: 1px solid #edf0f2;
}

.picker-item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  min-height: 34px;
  padding: 4px 6px;
  cursor: pointer;
  border-radius: 3px;

  &:hover,
  &.selected {
    background: #f3f9ff;
  }

  &.disabled {
    color: #c5c8ce;
    cursor: not-allowed;
    background: #fff;
  }
}

.picker-name {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.picker-price {
  color: #999;
  font-weight: 600;
}

.picker-preview {
  min-width: 0;
  min-height: 0;
  padding: 18px 16px;
  overflow-x: hidden;
  overflow-y: auto;
}

.preview-title {
  margin-bottom: 12px;
  color: #333;
  font-size: 14px;
  font-weight: 600;
  word-break: break-all;
}

.preview-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 28px;
  color: #999;
  font-size: 12px;

  strong {
    min-width: 72px;
    color: #515a6e;
    text-align: right;
    word-break: break-all;
  }
}

.green-text {
  color: #52c41a !important;
}

.blue-text {
  color: #2d8cf0 !important;
}

.empty-text {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  color: #999;
  font-size: 12px;
}

.ad-tag-box {
  margin-top: 8px;
  padding: 8px;
  color: #2d8cf0;
  font-size: 12px;
  background: #e6f7ff;
  border: 1px dashed #91d5ff;
  border-radius: 4px;
}

.ad-tag-title {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 16px;
}

.ad-tag-picker-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 16px;
  align-items: center;
  column-gap: 6px;
  margin-top: 6px;

  .row-delete {
    justify-self: center;
  }
}

.inner-purchase-tag {
  display: inline-flex;
  align-items: center;
  height: 14px;
  padding: 0 4px;
  color: #fff !important;
  font-size: 10px;
  line-height: 14px;
  background: #2d8cf0 !important;
  border-color: #2d8cf0 !important;
  border-radius: 2px;
}

.wide-section {
  margin-top: 18px;
}

.wide-card {
  padding: 12px;
  background: #fff;
  border: 1px solid #e8eaec;
  border-radius: 4px;
}

.activity-content {
  margin-top: 8px;
}

.activity-row {
  display: grid;
  grid-template-columns: 72px minmax(300px, 520px);
  align-items: center;
  margin-bottom: 8px;
  color: #333;
  font-size: 12px;
}

.footer-bar {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.footer-button {
  width: 96px;
}

@media (max-width: 1200px) {
  .payment-grid {
    grid-template-columns: repeat(3, 320px);
    overflow-x: auto;
  }
}
</style>

<style lang="less">
.recharge-subscription-popper,
.recharge-gold-picker-popper {
  .ivu-poptip-inner {
    white-space: normal;
  }

  .ivu-poptip-body,
  .ivu-poptip-body-content {
    overflow: visible;
  }
}
</style>
